import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  Mail, Lock, ArrowRight, Loader2, LogIn,
  BarChart3, FileText, Building2, Users, ShieldCheck, Sparkles, Target,
  HeartHandshake, BookOpen,
} from "lucide-react";
import { APP_NAME, APP_LOGO_URL, APP_LOGO_WIDE_URL } from "@/constants/appBranding";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_LANDING = {
  hero_title: "Cultiver l'impact.",
  hero_title_emphasis: "Relier",
  hero_title_suffix: "les acteurs.",
  hero_subtitle: "Centralisez vos données, générez vos bilans et valorisez l'impact de vos actions auprès de vos financeurs et partenaires.",
  hero_cta_label: "Découvrir le produit",
  hero_cta_caption: "Présentation, fonctionnalités et demande de démo",
  stat1_value: "350+", stat1_label: "Structures",
  stat2_value: "12 000", stat2_label: "Rapports générés",
  stat3_value: "-70%", stat3_label: "Temps de reporting",
  feature1_title: "Suivi des actions",
  feature1_desc: "Centralisez participations, bénévoles et partenaires pour chaque atelier ou événement.",
  feature2_title: "Bilans institutionnels simplifiés",
  feature2_desc: "Générez vos bilans semestriels et vos rapports financeurs en quelques clics.",
  feature3_title: "Multi-structures",
  feature3_desc: "Administrez plusieurs centres depuis un espace unique, avec données cloisonnées.",
  feature4_title: "Suivi individuel",
  feature4_desc: "Gardez la trace des rencontres, besoins et orientations des personnes accompagnées.",
};

const FEATURE_ICONS = [BarChart3, FileText, Building2, Users];

/**
 * Editorial login page — left column presents the app with hero logo + stats +
 * feature cards; right column hosts the login form. Stacks on mobile.
 *
 * Marketing copy (hero, stats, feature cards) is editable by super-admins via
 * `GET /api/site/landing` (public) and `PUT /api/site/admin/landing` (admin).
 * Defaults below are used until the API responds.
 */
export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [mustChange, setMustChange] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [content, setContent] = useState(DEFAULT_LANDING);
  const emailRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/site/landing`)
      .then((res) => setContent({ ...DEFAULT_LANDING, ...(res.data || {}) }))
      .catch(() => { /* keep defaults */ });
  }, []);

  const STATS = [
    { value: content.stat1_value, label: content.stat1_label },
    { value: content.stat2_value, label: content.stat2_label },
    { value: content.stat3_value, label: content.stat3_label },
  ];
  const FEATURE_CARDS = [
    { icon: FEATURE_ICONS[0], title: content.feature1_title, desc: content.feature1_desc },
    { icon: FEATURE_ICONS[1], title: content.feature2_title, desc: content.feature2_desc },
    { icon: FEATURE_ICONS[2], title: content.feature3_title, desc: content.feature3_desc },
    { icon: FEATURE_ICONS[3], title: content.feature4_title, desc: content.feature4_desc },
  ];

  const scrollToLogin = () => {
    emailRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => emailRef.current?.focus(), 400);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = { email, password };
      if (needsTotp && totpCode) payload.totp_code = totpCode;
      const { data } = await axios.post(`${API}/auth/login`, payload);
      localStorage.setItem("auth_token", data.token);
      localStorage.setItem("auth_user", JSON.stringify(data.user));
      // Ensure React Router re-mounts on "/" — replaceState alone doesn't
      // trigger a router refresh, which was landing new centers on 404.
      if (window.location.pathname === "/login" || window.location.pathname === "/") {
        window.history.replaceState({}, "", "/");
      } else {
        window.history.pushState({}, "", "/");
      }
      onLogin(data.token, data.user);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Structured errors from 2FA flow
      if (detail && typeof detail === "object" && detail.code === "TOTP_REQUIRED") {
        setNeedsTotp(true);
        setError("");
        setTimeout(() => document.getElementById("totp-code-input")?.focus(), 50);
      } else if (detail && typeof detail === "object" && detail.code === "TOTP_INVALID") {
        setNeedsTotp(true);
        setError(detail.message || "Code TOTP invalide");
        setTotpCode("");
      } else if (detail && typeof detail === "object" && detail.code === "PASSWORD_CHANGE_REQUIRED") {
        setMustChange(true);
        setError("");
        setTimeout(() => document.getElementById("new-password-input")?.focus(), 50);
      } else {
        setError(typeof detail === "string" ? detail : (detail?.message || "Identifiants incorrects"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForcedChange = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) { setError("Le nouveau mot de passe doit contenir au moins 8 caractères."); return; }
    if (newPassword !== newPassword2) { setError("Les deux mots de passe ne correspondent pas."); return; }
    setLoading(true);
    try {
      const body = { email, current_password: password, new_password: newPassword };
      if (totpCode) body.totp_code = totpCode;
      await axios.post(`${API}/auth/force-password-change`, body);
      const { data } = await axios.post(`${API}/auth/login`, { email, password: newPassword, ...(totpCode ? { totp_code: totpCode } : {}) });
      localStorage.setItem("auth_token", data.token);
      localStorage.setItem("auth_user", JSON.stringify(data.user));
      window.history.replaceState({}, "", "/");
      onLogin(data.token, data.user);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && (detail.code === "TOTP_REQUIRED" || detail.code === "TOTP_INVALID")) {
        setNeedsTotp(true);
        setError(detail.code === "TOTP_INVALID" ? (detail.message || "Code TOTP invalide") : "Saisissez votre code de double authentification.");
      } else {
        setError(typeof detail === "string" ? detail : (detail?.message || "Changement de mot de passe impossible"));
      }
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = email && password && !loading && (!needsTotp || totpCode.length === 6) && (!mustChange || (newPassword.length >= 8 && newPassword === newPassword2));

  const handleResetRequest = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Saisissez votre email puis cliquez à nouveau sur 'Mot de passe oublié ?'");
      return;
    }
    setResetBusy(true);
    try {
      await axios.post(`${API}/auth/request-password-reset`, { email: email.trim().toLowerCase() });
      setResetSent(true);
    } catch {
      setResetSent(true); // generic response either way
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen relative overflow-hidden text-slate-100"
      style={{
        background:
          "linear-gradient(135deg, #061a2f 0%, #0A2D4D 45%, #0e3a63 100%)",
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* Decorative blurred blobs */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(circle, #00A8B5 0%, transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-52 -right-32 h-[620px] w-[620px] rounded-full opacity-25 blur-3xl"
        style={{ background: "radial-gradient(circle, #53C268 0%, transparent 70%)" }}
      />

      {/* Shortcut "Découvrir le produit" — top-left corner (public landing) */}
      <a
        data-testid="login-about-shortcut"
        href="/a-propos"
        className="group fixed top-5 left-5 md:top-6 md:left-8 z-20 inline-flex items-center gap-2 px-4 md:px-5 py-2 md:py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold shadow-lg shadow-black/20 hover:shadow-xl hover:-translate-y-0.5 border border-white/20 backdrop-blur-sm transition-all"
      >
        <Sparkles className="h-4 w-4" />
        <span className="hidden sm:inline">Découvrir le produit</span>
        <span className="sm:hidden">Le produit</span>
        <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
      </a>

      {/* Shortcut "Se connecter" — top-right corner */}
      <button
        data-testid="login-shortcut-button"
        type="button"
        onClick={scrollToLogin}
        className="group fixed top-5 right-5 md:top-6 md:right-8 z-20 inline-flex items-center gap-2 px-4 md:px-5 py-2 md:py-2.5 rounded-full bg-teal-700 hover:bg-[#019aa6] text-white text-sm font-semibold shadow-lg shadow-black/30 hover:shadow-xl hover:shadow-black/40 hover:-translate-y-0.5 transition-all"
      >
        <LogIn className="h-4 w-4" />
        Se connecter
        <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
      </button>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-10 lg:py-14 pt-20 lg:pt-14 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 lg:items-center lg:min-h-screen">

        {/* LEFT — brand & presentation */}
        <div className="space-y-8">
          {/* Hero logo lockup */}
          <div className="flex items-center gap-4">
            <img
              data-testid="login-brand-logo"
              src={APP_LOGO_WIDE_URL}
              alt={APP_NAME}
              className="h-24 md:h-32 w-auto object-contain rounded-2xl bg-white p-3 shadow-2xl shadow-black/40 border border-white/10 ring-1 ring-white/5"
            />
            <div>
              <span
                data-testid="login-brand-name"
                className="sr-only"
              >
                {APP_NAME}
              </span>
              <div className="flex flex-col gap-1.5">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-teal-700/15 text-[#7ce6ee] text-[11px] font-semibold tracking-wide w-fit border border-[#00A8B5]/30">
                  <span className="h-1.5 w-1.5 rounded-full bg-teal-700" />
                  Le cœur numérique de vos projets solidaires
                </span>
                <span className="text-[11px] italic text-slate-300 pl-1">
                  Cultiver l&apos;impact. Relier les acteurs.
                </span>
              </div>
            </div>
          </div>

          {/* Editorial title */}
          <div>
            <h1
              data-testid="login-title"
              className="text-white text-4xl md:text-5xl leading-[1.05] max-w-xl"
              style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
            >
              {content.hero_title} <span className="italic text-[#7ce6ee]">{content.hero_title_emphasis}</span> {content.hero_title_suffix}
            </h1>
            <p className="text-slate-300 text-base md:text-lg mt-4 max-w-xl leading-relaxed">
              {content.hero_subtitle}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <a
                href="/a-propos"
                data-testid="login-about-inline"
                className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white hover:bg-teal-700 text-[#0A2D4D] hover:text-white text-sm font-semibold shadow-md hover:-translate-y-0.5 transition-all"
              >
                <Sparkles className="h-4 w-4 text-[#00A8B5] group-hover:text-white transition-colors" />
                {content.hero_cta_label}
                <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <span className="text-xs text-slate-700">
                {content.hero_cta_caption}
              </span>
            </div>
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-3 gap-3 max-w-xl">
            {STATS.map((s) => (
              <div
                key={s.label}
                className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl px-4 py-3 text-center"
              >
                <div
                  className="text-xl md:text-2xl font-bold text-[#7ce6ee]"
                  style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
                >
                  {s.value}
                </div>
                <div className="text-[11px] text-slate-700 uppercase tracking-wider mt-0.5">
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          {/* Feature cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
            {FEATURE_CARDS.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                data-testid={`feature-card-${title}`}
                className="group bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-4 hover:bg-white/10 hover:border-[#00A8B5]/40 hover:shadow-lg hover:shadow-black/20 hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-[#ccfbf1] flex items-center justify-center flex-shrink-0 group-hover:bg-[#0d9488] group-hover:text-white text-[#0d9488] transition-colors">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-white text-sm">{title}</div>
                    <p className="text-xs text-slate-300 mt-0.5 leading-relaxed">{desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Besoin → Action loop highlight */}
          <div
            data-testid="landing-besoin-loop"
            className="max-w-xl bg-gradient-to-br from-[#0d9488] to-[#0f766e] text-white rounded-2xl p-5 shadow-lg shadow-teal-900/20"
          >
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
                <Target className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-sm md:text-base">
                  Du terrain au bilan, sans rien perdre
                </div>
                <p className="text-xs md:text-sm text-white/85 mt-1 leading-relaxed">
                  Chaque besoin remonté est relié à l&apos;action ou la fiche qui y répond.
                  Taux de couverture affiché en temps réel et intégré au bilan institutionnel.
                </p>
              </div>
            </div>
          </div>

          {/* Sectors strip — pitch decliné par vertical */}
          <div data-testid="landing-sectors" className="max-w-xl">
            <div className="text-xs font-semibold text-slate-200 uppercase tracking-wider mb-3">
              Pensé pour
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {[
                { icon: HeartHandshake, label: "Centres sociaux", to: "/secteurs#centres-sociaux" },
                { icon: Building2, label: "CCAS & collectivités", to: "/secteurs#ccas" },
                { icon: BookOpen, label: "Bibliothèques municipales", to: "/secteurs#bibliotheques" },
                { icon: Users, label: "Associations & ESS", to: "/secteurs#associations" },
              ].map(({ icon: Icon, label, to }) => (
                <a
                  key={label}
                  href={to}
                  data-testid={`landing-sector-${label}`}
                  className="flex items-center gap-2.5 px-3 py-3 rounded-xl bg-white/95 border border-white/20 shadow-md hover:border-[#00A8B5] hover:bg-white hover:shadow-lg hover:-translate-y-0.5 transition-all text-sm font-semibold text-[#0A2D4D]"
                >
                  <span className="h-8 w-8 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-4 w-4 text-teal-600" />
                  </span>
                  <span className="truncate">{label}</span>
                </a>
              ))}
            </div>
            <a
              href="/secteurs"
              data-testid="landing-sectors-cta"
              className="inline-flex items-center gap-1 text-xs text-[#7ce6ee] hover:text-white font-semibold mt-3"
            >
              Voir tous les usages métier <ArrowRight className="h-3 w-3" />
            </a>
          </div>

          <p className="hidden lg:flex items-center gap-1.5 text-xs text-slate-700">
            <ShieldCheck className="h-3.5 w-3.5" /> Données hébergées en France · Conformité RGPD · Accès par centre.
          </p>
        </div>

        {/* RIGHT — login card */}
        <div className="w-full max-w-md justify-self-center lg:justify-self-end">
          <div className="bg-white/85 backdrop-blur-xl border border-teal-900/5 rounded-3xl shadow-[0_20px_60px_-20px_rgba(13,148,136,0.25)] p-8 md:p-10">
            <div className="flex justify-center mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#ccfbf1] text-[#0f766e] text-xs font-medium tracking-wide">
                <span className="h-1.5 w-1.5 rounded-full bg-[#0d9488]" />
                Connexion sécurisée
              </span>
            </div>
            <h2
              className="text-center text-[#0f172a] text-2xl md:text-3xl leading-tight"
              style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
            >
              Bon retour <span className="italic">parmi nous</span>
            </h2>
            <p className="text-center text-[#64748b] text-sm mt-2">
              Connectez-vous pour accéder à votre espace.
            </p>

            <form onSubmit={mustChange ? handleForcedChange : handleSubmit} className="mt-7 space-y-4">
              {mustChange && (
                <div data-testid="login-force-change" className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <p className="text-sm font-semibold text-amber-900">Nouveau mot de passe requis</p>
                  <p className="text-xs text-amber-900/80">Ce compte utilise encore un mot de passe par défaut. Choisissez un mot de passe fort (8 caractères minimum) pour continuer : vous serez connecté juste après.</p>
                  <input id="new-password-input" data-testid="login-new-password" type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Nouveau mot de passe"
                    className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:border-[#0d9488] focus:ring-4 focus:ring-[#0d9488]/10" />
                  <input data-testid="login-new-password-confirm" type="password" autoComplete="new-password" value={newPassword2} onChange={(e) => setNewPassword2(e.target.value)} placeholder="Confirmer le nouveau mot de passe"
                    className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:border-[#0d9488] focus:ring-4 focus:ring-[#0d9488]/10" />
                </div>
              )}
              <div>
                <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-[#64748b] mb-1.5">
                  Email
                </label>
                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8] group-focus-within:text-[#0d9488] transition-colors" />
                  <input
                    data-testid="login-email-input"
                    id="email"
                    ref={emailRef}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.fr"
                    autoFocus
                    className="w-full pl-11 pr-4 py-3.5 bg-[#f0fdfa] border border-[#ccfbf1] rounded-xl text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:border-[#0d9488] focus:ring-4 focus:ring-[#0d9488]/10 transition-all"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-xs font-semibold uppercase tracking-wider text-[#64748b] mb-1.5">
                  Mot de passe
                </label>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8] group-focus-within:text-[#0d9488] transition-colors" />
                  <input
                    data-testid="login-password-input"
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-11 pr-4 py-3.5 bg-[#f0fdfa] border border-[#ccfbf1] rounded-xl text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:border-[#0d9488] focus:ring-4 focus:ring-[#0d9488]/10 transition-all"
                  />
                </div>
              </div>

              {needsTotp && (
                <div data-testid="login-totp-section">
                  <label htmlFor="totp-code-input" className="block text-xs font-semibold uppercase tracking-wider text-[#64748b] mb-1.5">
                    Code de double authentification
                  </label>
                  <input
                    data-testid="login-totp-input"
                    id="totp-code-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    autoComplete="one-time-code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="6 chiffres"
                    className="w-full px-4 py-3.5 bg-[#f0fdfa] border border-[#ccfbf1] rounded-xl text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:border-[#0d9488] focus:ring-4 focus:ring-[#0d9488]/10 transition-all text-center text-xl tracking-[0.5em] font-mono"
                  />
                  <p className="mt-1.5 text-[11px] text-[#64748b]">
                    Saisissez le code à 6 chiffres affiché dans votre application d&apos;authentification.
                  </p>
                </div>
              )}

              {error && (
                <div
                  data-testid="login-error"
                  className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5"
                >
                  {error}
                </div>
              )}

              <button
                data-testid="login-submit-button"
                type="submit"
                disabled={!canSubmit}
                className="group w-full flex items-center justify-center gap-2 py-3.5 bg-[#0f766e] hover:bg-[#115e59] disabled:bg-slate-500 disabled:cursor-not-allowed text-white font-semibold rounded-full shadow-lg shadow-teal-900/20 transition-all hover:shadow-xl hover:shadow-teal-900/30 hover:-translate-y-0.5"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Connexion…
                  </>
                ) : (
                  <>
                    {mustChange ? "Enregistrer et se connecter" : "Se connecter"}
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-4 text-center">
              {resetSent ? (
                <div
                  data-testid="reset-sent-banner"
                  className="inline-block px-4 py-2.5 bg-[#f0fdfa] border border-[#ccfbf1] rounded-xl text-[12px] text-[#065f46] leading-snug"
                >
                  ✓ Demande transmise. Votre super-administrateur vous communiquera un nouveau mot de passe sous peu.
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={handleResetRequest}
                  disabled={resetBusy}
                  className="text-xs text-[#64748b] hover:text-[#0d9488] transition-colors underline-offset-2 hover:underline"
                >
                  {resetBusy ? "Envoi…" : "Mot de passe oublié ?"}
                </button>
              )}
            </div>

            <p className="mt-4 text-center text-[11px] text-[#64748b]">
              Votre centre sera identifié automatiquement à partir de votre email.
            </p>
          </div>

          <p className="mt-5 text-center text-xs text-[#64748b]">
            © {new Date().getFullYear()} {APP_NAME} — Cultiver l&apos;impact. Relier les acteurs.
          </p>
          <p className="mt-1 text-center text-xs">
            <a href="/fonctionnalites" data-testid="login-features-link" className="text-[#64748b] hover:text-[#0d9488] transition-colors">
              Fonctionnalités
            </a>
            <span className="mx-2 text-[#cbd5e1]">·</span>
            <a href="/tarifs" data-testid="login-pricing-link" className="text-[#64748b] hover:text-[#0d9488] transition-colors">
              Tarifs
            </a>
            <span className="mx-2 text-[#cbd5e1]">·</span>
            <a href="/blog" data-testid="login-blog-link" className="text-[#64748b] hover:text-[#0d9488] transition-colors">
              Blog
            </a>
            <span className="mx-2 text-[#cbd5e1]">·</span>
            <a href="/confidentialite" data-testid="login-privacy-link" className="text-[#64748b] hover:text-[#0d9488] transition-colors">
              Confidentialité (RGPD)
            </a>
            <span className="mx-2 text-[#cbd5e1]">·</span>
            <a href="/mentions-legales" data-testid="login-legal-link" className="text-[#64748b] hover:text-[#0d9488] transition-colors">
              Mentions légales
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
